import {
  getIsCarrotCardEligible,
  getMemberHasLegalPartner,
  getIsUsa,
  getSubjectToAppLockdown
} from "../../../reducers/userInfo"
import { getHasAca, getIsSamlActive, getSupportsDeductiblePlans } from "../../../reducers/companyInfo"
import { getCompanyHasExternallyManagedDeductibleEnabled } from "../../../derivedSelectors/deductibleSelectors"
import { getBenefitConfiguration } from "../../../reducers/benefitConfiguration"
import { getToggleActionForType } from "../../../actions/testUserActions"
import TestUserActionTypes from "actions/testUserActionTypes"

const getFeatureConfigSelector = (state) => {
  return getBenefitConfiguration(state).featureConfig
}
const getCoverageConfigSelector = (state) => {
  return getBenefitConfiguration(state).coverageConfig
}

const mapping = {
  //feature config
  bank_info: {
    selector: (state) => getFeatureConfigSelector(state)?.isBankInfoEnabled,
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_IS_BANK_INFO_FEATURE_ENABLED)
  },
  carrot_card: {
    selector: (state) => getIsCarrotCardEligible(state),
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_IS_CARROT_CARD_ELIGIBLE)
  },
  carrot_rx: {
    selector: (state) => getFeatureConfigSelector(state)?.isCarrotRxEnabled,
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_IS_CARROT_RX_FEATURE_ENABLED)
  },
  custom_coverage_content: {
    selector: (state) => getFeatureConfigSelector(state)?.isCustomCoverageContentEnabled,
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_IS_CUSTOM_COVERAGE_CONTENT_FEATURE_ENABLED)
  },
  gender_affirming_care_coverage: {
    selector: (state) => getFeatureConfigSelector(state)?.isGenderAffirmingCareCoverageEnabled,
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_IS_GENDER_AFFIRMING_CARE_COVERAGE_FEATURE_ENABLED)
  },
  low_t_coverage: {
    selector: (state) => getFeatureConfigSelector(state)?.isLowTCoverageEnabled,
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_IS_LOW_T_COVERAGE_FEATURE_ENABLED)
  },
  menopause_coverage: {
    selector: (state) => getFeatureConfigSelector(state)?.isMenopauseCoverageEnabled,
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_IS_MENOPAUSE_COVERAGE_FEATURE_ENABLED)
  },
  partner_access: {
    selector: (state) => getFeatureConfigSelector(state)?.isPartnerCredentialAccessEnabled,
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_IS_PARTNER_CREDENTIAL_ACCESS_ENABLED)
  },
  partner_feature: {
    selector: (state) => getFeatureConfigSelector(state)?.isPartnerEnabled,
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_IS_PARTNER_FEATURE_ENABLED)
  },
  show_donor_content: {
    selector: (state) => getFeatureConfigSelector(state)?.isDonorEnabled,
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_IS_DONOR_FEATURE_ENABLED)
  },
  show_lgbtq_content: {
    selector: (state) => getFeatureConfigSelector(state)?.isLgtbqEnabled,
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_IS_LGBTQ_FEATURE_ENABLED)
  },
  show_support_center: {
    selector: (state) => getFeatureConfigSelector(state)?.isSupportCenterEnabled,
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_IS_SUPPORT_CENTER_FEATURE_ENABLED)
  },
  gross_up: {
    selector: (state) => getFeatureConfigSelector(state)?.isGrossUpEnabled,
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_IS_GROSS_UP_FEATURE_ENABLED)
  },
  //coverage configs
  copay: {
    selector: (state) => getCoverageConfigSelector(state)?.isCopayEnabled,
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_IS_COVERAGE_COPAY_ENABLED)
  },
  infertility: {
    selector: (state) => getCoverageConfigSelector(state)?.isInfertilityEnabled,
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGLLE_IS_INFERTILITY_COVERAGE_ENABLED)
  },
  legal_partners_eligible: {
    selector: (state) => getCoverageConfigSelector(state)?.areLegalPartnersEligible,
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_LEGAL_PARTNERS_ELIGIBLE)
  },
  member_and_partner_eligible: {
    selector: (state) => getCoverageConfigSelector(state)?.isMemberAndPartnerEligible,
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_IS_MEMBER_AND_PARTNER_ELIGIBLE_COVERAGE)
  },
  partner_eligible_if_on_customer_medical_plan: {
    selector: (state) => getCoverageConfigSelector(state)?.isPartnerEligibleIfOnCustomerMedicalPlan,
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_IS_PARTNER_ELIGIBLE_IF_ON_CUSTOMER_MEDICAL_PLAN)
  },
  //other
  aca_applies: {
    selector: (state) => getHasAca(state),
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_HAS_ACA)
  },
  external_deductible: {
    selector: (state) => getCompanyHasExternallyManagedDeductibleEnabled(state),
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_EXTERNAL_DEDUCTIBLE)
  },
  has_legal_partner: {
    selector: (state) => getMemberHasLegalPartner(state),
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_MEMBER_HAS_LEGAL_PARTNER)
  },
  sso: {
    selector: (state) => getIsSamlActive(state),
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_IS_SAML_ACTIVE)
  },
  subject_to_app_lockdown: {
    selector: (state) => getSubjectToAppLockdown(state),
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_SUBJECT_TO_APP_LOCKDOWN)
  },
  supports_deductible_plans: {
    selector: (state) => getSupportsDeductiblePlans(state),
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_SUPPORTS_DEDUCTIBLE_PLANS)
  },
  us: {
    selector: (state) => getIsUsa(state),
    toggleAction: getToggleActionForType(TestUserActionTypes.TOGGLE_IS_USA)
  }
}

export function getConditionsSelector(dispatch): (state) => Record<string, { value: boolean; toggle: () => void }> {
  return (state) =>
    Object.keys(mapping).reduce((accumulator, key) => {
      accumulator[key] = { value: mapping[key].selector(state), toggle: () => dispatch(mapping[key].toggleAction()) }
      return accumulator
    }, {})
}

export const getToggleActionForKey = (conditionKey) => mapping[conditionKey].toggleAction
